import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Container,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import {
  useDeliveryAreaActions,
  useDeliveryAreaData,
} from 'src/context/providers/DeliveryAreaContext';
import { DirectionFlags } from '../../orderPhysical/direction-picker-modal';
import DeliveryAreaDrawer from '../delivery-area-drawer';
import { AREA_DELIVERY_LABELS, AREA_DELIVERY_OPTIONS } from './data';

const ALL = 'all';

const formatUSD = (value) => `$${Number(value ?? 0).toFixed(2)}`;

export default function DeliveryAreaListView() {
  const settings = useSettingsContext();
  const { areas, loading } = useDeliveryAreaData();
  const { getDeliveryAreas, setDeliveryAreaActive, deleteDeliveryArea } = useDeliveryAreaActions();

  const [delivery, setDelivery] = useState(ALL);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const reload = () =>
    getDeliveryAreas({ destination_delivery: delivery === ALL ? undefined : delivery });

  useEffect(() => {
    getDeliveryAreas({ destination_delivery: delivery === ALL ? undefined : delivery });
  }, [delivery, getDeliveryAreas]);

  const visibleAreas = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return areas;
    return areas.filter((area) =>
      [area.name, area.city, area.description].some((value) =>
        value?.toLowerCase().includes(term)
      )
    );
  }, [areas, search]);

  const openDrawer = (area) => {
    setEditing(area);
    setDrawerOpen(true);
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Delivery Areas"
        action={
          <Button
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={() => openDrawer(null)}
          >
            New Delivery Area
          </Button>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <Card>
        <Tabs value={delivery} onChange={(_, value) => setDelivery(value)} sx={{ px: 2.5 }}>
          <Tab value={ALL} label="All" />
          {AREA_DELIVERY_OPTIONS.map((option) => (
            <Tab
              key={option.value}
              value={option.value}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DirectionFlags direction={option.direction} size={16} />
                  <span>{option.label}</span>
                </Stack>
              }
            />
          ))}
        </Tabs>

        <Stack sx={{ p: 2.5 }}>
          <TextField
            fullWidth
            placeholder="Search name, city or description…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        {loading && <LinearProgress color="success" />}

        <TableContainer>
          <Scrollbar>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>City / state</TableCell>
                  <TableCell>Delivery type</TableCell>
                  <TableCell align="right">Price per group</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleAreas.map((area) => {
                  const option = AREA_DELIVERY_LABELS[area.destination_delivery];
                  return (
                    <TableRow key={area.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDrawer(area)}>
                      <TableCell>
                        <Typography variant="subtitle2">{area.name}</Typography>
                        {area.description && (
                          <Typography variant="caption" color="text.secondary">
                            {area.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{area.city || '-'}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DirectionFlags direction={area.direction} size={16} />
                          <span>{option?.label ?? area.destination_delivery}</span>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{formatUSD(area.price_usd)}</TableCell>
                      <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                        <Switch
                          checked={area.is_active}
                          onChange={(event) => setDeliveryAreaActive(area.id, event.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => setDeleting(area)}>
                            <Iconify icon="solar:trash-bin-trash-bold" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loading && !visibleAreas.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No delivery areas yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>
      </Card>

      <DeliveryAreaDrawer
        open={drawerOpen}
        area={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={reload}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="Delete delivery area"
        content={
          <>
            Delete <strong>{deleting?.name}</strong>? Groups already registered with it keep their
            delivery fee.
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              await deleteDeliveryArea(deleting.id);
              setDeleting(null);
            }}
          >
            Delete
          </Button>
        }
      />
    </Container>
  );
}
